import * as React from 'react';
import type { LegendPayload } from 'recharts';
import * as RechartsPrimitive from 'recharts';
import type { VerticalAlignmentType } from 'recharts/types/component/DefaultLegendContent';

import { createSafeContext } from '@/lib/create-safe-context';
import { cn } from '@/lib/utils';

// Format: { THEME_NAME: CSS_SELECTOR }
const THEMES = { light: '', dark: '.dark' } as const;
const SAFE_CSS_COLOR_PATTERN =
  /^(?:#[\da-fA-F]{3,8}|(?:rgb|hsl|oklch|color-mix)\([^)]+\)|var\(--[a-zA-Z0-9-_]+\)|[a-zA-Z]+)$/;

/**
 * Categorical chart palette resolved from the design tokens. Mode-aware via
 * `--chart-*` tokens (see src/tokens.css). Use this palette by default; fall
 * back to `CHART_NEUTRAL_COLOR` for baseline / "other" series.
 *
 * Hue order: teal (brand) → violet → amber → pink → sky.
 * Each hue meets >=3:1 against both #ffffff (light) and #161616 (dark).
 */
export const CHART_PALETTE = [
  'var(--chart-1)',
  'var(--chart-2)',
  'var(--chart-3)',
  'var(--chart-4)',
  'var(--chart-5)',
] as const;

/**
 * Per-instance default colours. Pass the chart's stable identity for each item;
 * items without an identity must use their index (and cannot survive reorder).
 * Duplicate keys share a colour. Removed keys release their palette slots.
 */
export function useChartKeyColors(
  keys: readonly (string | number)[],
  palette: readonly string[] = CHART_PALETTE,
): ReadonlyMap<string | number, string> {
  const resolvedPalette = palette.length ? palette : CHART_PALETTE;
  const signature = JSON.stringify([keys, resolvedPalette]);
  const [previous, setPrevious] = React.useState(() => ({
    signature: '',
    slots: new Map<string | number, number>(),
    colors: new Map<string | number, string>(),
  }));
  if (previous.signature !== signature) {
    const slots = new Map<string | number, number>();
    const present = new Set(keys);
    for (const [key, slot] of previous.slots) {
      if (present.has(key) && slot < resolvedPalette.length)
        slots.set(key, slot);
    }
    const used = new Set(slots.values());
    for (const key of present) {
      if (slots.has(key)) continue;
      let slot = 0;
      while (slot < resolvedPalette.length && used.has(slot)) slot++;
      if (slot === resolvedPalette.length)
        slot = slots.size % resolvedPalette.length;
      slots.set(key, slot);
      used.add(slot);
    }
    // React discards this state with an abandoned render; never mutate the
    // previous map or a ref while rendering.
    const colors = new Map(
      Array.from(slots, ([key, slot]) => [key, resolvedPalette[slot]]),
    );
    setPrevious({ signature, slots, colors });
    return colors;
  }
  return previous.colors;
}

/** Neutral / baseline color for "other" or background data series. */
export const CHART_NEUTRAL_COLOR = 'var(--slate-500)';

export type ChartConfig = {
  [k in string]: {
    label?: React.ReactNode;
    icon?: React.ComponentType;
  } & (
    | { color?: string; theme?: never }
    | { color?: never; theme: Record<keyof typeof THEMES, string> }
  );
};

type ChartContextProps = {
  config: ChartConfig;
};

const [ChartContext, useChart] =
  createSafeContext<ChartContextProps>('ChartContainer');

function ChartContainer({
  id,
  className,
  children,
  config,
  ...props
}: React.ComponentProps<'div'> & {
  config: ChartConfig;
  children: React.ComponentProps<
    typeof RechartsPrimitive.ResponsiveContainer
  >['children'];
}) {
  const uniqueId = React.useId();
  const chartId = `chart-${id || uniqueId.replace(/:/g, '')}`;
  const contextValue = React.useMemo<ChartContextProps>(
    () => ({ config }),
    [config],
  );

  return (
    <ChartContext.Provider value={contextValue}>
      <div
        data-slot="chart"
        data-chart={chartId}
        className={cn(
          "[&_.recharts-cartesian-axis-tick_text]:fill-muted-foreground [&_.recharts-cartesian-grid_line[stroke='#ccc']]:stroke-border/50 [&_.recharts-curve.recharts-tooltip-cursor]:stroke-border [&_.recharts-polar-grid_[stroke='#ccc']]:stroke-border [&_.recharts-radial-bar-background-sector]:fill-muted [&_.recharts-rectangle.recharts-tooltip-cursor]:fill-muted [&_.recharts-reference-line_[stroke='#ccc']]:stroke-border flex aspect-video justify-center text-xs [&_.recharts-dot[stroke='#fff']]:stroke-transparent [&_.recharts-layer]:outline-hidden [&_.recharts-sector]:outline-hidden [&_.recharts-sector[stroke='#fff']]:stroke-transparent [&_.recharts-surface]:outline-hidden",
          className,
        )}
        {...props}
      >
        <ChartStyle id={chartId} config={config} />
        <RechartsPrimitive.ResponsiveContainer
          minWidth={0}
          initialDimension={{ width: 1, height: 1 }}
        >
          {children}
        </RechartsPrimitive.ResponsiveContainer>
      </div>
    </ChartContext.Provider>
  );
}

const ChartStyle = ({ id, config }: { id: string; config: ChartConfig }) => {
  const colorConfig = React.useMemo(
    () =>
      Object.entries(config).filter(
        ([, config]) => config.theme || config.color,
      ),
    [config],
  );
  const cssText = React.useMemo(() => {
    if (!colorConfig.length) {
      return '';
    }

    return Object.entries(THEMES)
      .map(
        ([theme, prefix]) => `
${prefix} [data-chart="${id.replace(/["\\]/g, '\\$&')}"] {
${colorConfig
  .map(([key, itemConfig]) => {
    const color =
      itemConfig.theme?.[theme as keyof typeof itemConfig.theme] ||
      itemConfig.color;
    return color && SAFE_CSS_COLOR_PATTERN.test(color)
      ? `  --color-${key}: ${color};`
      : null;
  })
  .filter(Boolean)
  .join('\n')}
}
`,
      )
      .join('\n');
  }, [colorConfig, id]);

  if (!colorConfig.length) {
    return null;
  }

  return <style>{cssText}</style>;
};

const ChartTooltip = RechartsPrimitive.Tooltip;

function ChartTooltipContent({
  active,
  payload,
  className,
  indicator = 'dot',
  hideLabel = false,
  hideIndicator = false,
  label,
  labelFormatter,
  labelClassName,
  formatter,
  color,
  nameKey,
  labelKey,
}: React.ComponentProps<'div'> & {
  active?: boolean;
  payload?: ReadonlyArray<{
    value?: string | number;
    name?: string;
    dataKey?: string | number;
    type?: string;
    color?: string;
    payload?: Record<string, unknown>;
    fill?: string;
  }>;
  label?: string | number;
  labelFormatter?: (
    label: React.ReactNode,
    payload: ReadonlyArray<unknown>,
  ) => React.ReactNode;
  labelClassName?: string;
  formatter?: (
    value: string | number,
    name: string,
    item: unknown,
    index: number,
    payload: unknown,
  ) => React.ReactNode;
  color?: string;
  hideLabel?: boolean;
  hideIndicator?: boolean;
  indicator?: 'line' | 'dot' | 'dashed';
  nameKey?: string;
  labelKey?: string;
}) {
  const { config } = useChart();
  const visiblePayload = React.useMemo(
    () => (payload ? getVisiblePayloadItems(payload) : []),
    [payload],
  );

  const tooltipLabel = React.useMemo(() => {
    if (hideLabel || !visiblePayload.length) {
      return null;
    }

    const [item] = visiblePayload;
    const key = `${labelKey || item?.dataKey || item?.name || 'value'}`;
    const itemConfig = getPayloadConfigFromPayload(config, item, key);
    const value =
      !labelKey && typeof label === 'string'
        ? config[label as keyof typeof config]?.label || label
        : itemConfig?.label;

    if (labelFormatter) {
      return (
        <div className={cn('font-medium', labelClassName)}>
          {labelFormatter(value, visiblePayload)}
        </div>
      );
    }

    if (!value) {
      return null;
    }

    return <div className={cn('font-medium', labelClassName)}>{value}</div>;
  }, [
    label,
    labelFormatter,
    visiblePayload,
    hideLabel,
    labelClassName,
    config,
    labelKey,
  ]);

  if (!active || !visiblePayload.length) {
    return null;
  }

  const nestLabel = visiblePayload.length === 1 && indicator !== 'dot';

  return (
    <div
      className={cn(
        'border-border/50 bg-background gap-1.5 rounded-lg border px-2.5 py-1.5 text-xs shadow-xl grid min-w-[8rem] items-start',
        className,
      )}
    >
      {!nestLabel ? tooltipLabel : null}
      <div className="grid gap-1.5">
        {visiblePayload.map((item, index) => {
          const key = `${nameKey || item.name || item.dataKey || 'value'}`;
          const itemKey = getPayloadItemKey(item, index, key);
          const itemConfig = getPayloadConfigFromPayload(config, item, key);
          const indicatorColor = color || item.payload?.fill || item.color;

          return (
            <div
              key={itemKey}
              className={cn(
                '[&>svg]:text-muted-foreground flex w-full flex-wrap items-stretch gap-2 [&>svg]:h-2.5 [&>svg]:w-2.5',
                indicator === 'dot' && 'items-center',
              )}
            >
              {formatter && item?.value !== undefined && item.name ? (
                formatter(item.value, item.name, item, index, item.payload)
              ) : (
                <>
                  {itemConfig?.icon ? (
                    <itemConfig.icon />
                  ) : (
                    !hideIndicator && (
                      <div
                        className={cn(
                          'shrink-0 rounded-[2px] border-(--color-border) bg-(--color-bg)',
                          {
                            'h-2.5 w-2.5': indicator === 'dot',
                            'w-1': indicator === 'line',
                            'w-0 border-[1.5px] border-dashed bg-transparent':
                              indicator === 'dashed',
                            'my-0.5': nestLabel && indicator === 'dashed',
                          },
                        )}
                        style={
                          {
                            '--color-bg': indicatorColor,
                            '--color-border': indicatorColor,
                          } as React.CSSProperties
                        }
                      />
                    )
                  )}
                  <div
                    className={cn(
                      'flex flex-1 justify-between leading-none',
                      nestLabel ? 'items-end' : 'items-center',
                    )}
                  >
                    <div className="grid gap-1.5">
                      {nestLabel ? tooltipLabel : null}
                      <span className="text-muted-foreground">
                        {itemConfig?.label || item.name}
                      </span>
                    </div>
                    {item.value !== undefined && item.value !== null && (
                      <span className="text-foreground font-mono font-medium tabular-nums">
                        {item.value.toLocaleString()}
                      </span>
                    )}
                  </div>
                </>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}

const ChartLegend = RechartsPrimitive.Legend;

function ChartLegendContent({
  className,
  hideIcon = false,
  payload,
  verticalAlign = 'bottom',
  nameKey,
}: React.ComponentProps<'div'> & {
  payload?: ReadonlyArray<LegendPayload>;
  verticalAlign?: VerticalAlignmentType;
  hideIcon?: boolean;
  nameKey?: string;
}) {
  const { config } = useChart();

  if (!payload?.length) {
    return null;
  }

  const visiblePayload = getVisiblePayloadItems(payload);

  return (
    <div
      className={cn(
        'flex items-center justify-center gap-4',
        verticalAlign === 'top' ? 'pb-3' : 'pt-3',
        className,
      )}
    >
      {visiblePayload.map((item, index) => {
        const key = `${nameKey || item.dataKey || 'value'}`;
        const itemKey = getPayloadItemKey(item, index, key);
        const itemConfig = getPayloadConfigFromPayload(config, item, key);

        return (
          <div
            key={itemKey}
            className={cn(
              '[&>svg]:text-muted-foreground flex items-center gap-1.5 [&>svg]:h-3 [&>svg]:w-3',
            )}
          >
            {itemConfig?.icon && !hideIcon ? (
              <itemConfig.icon />
            ) : (
              <div
                className="h-2 w-2 shrink-0 rounded-[2px]"
                style={{
                  backgroundColor: item.color,
                }}
              />
            )}
            {itemConfig?.label}
          </div>
        );
      })}
    </div>
  );
}

function getVisiblePayloadItems<
  T extends { dataKey?: unknown; type?: unknown },
>(items: ReadonlyArray<T>): T[] {
  const seenDataKeys = new Set<unknown>();
  const visibleItems: T[] = [];

  for (const item of items) {
    if (item.type === 'none') continue;
    if (item.dataKey !== undefined && item.dataKey !== null) {
      if (seenDataKeys.has(item.dataKey)) continue;
      seenDataKeys.add(item.dataKey);
    }
    visibleItems.push(item);
  }

  return visibleItems;
}

function getPayloadItemKey(
  item: { dataKey?: unknown; name?: unknown; value?: unknown },
  index: number,
  fallback: string,
): string {
  if (item.dataKey !== undefined && item.dataKey !== null) {
    return `${item.dataKey}`;
  }

  return `${fallback}-${String(item.name ?? item.value ?? 'item')}-${index}`;
}

function getPayloadConfigFromPayload(
  config: ChartConfig,
  payload: unknown,
  key: string,
) {
  if (typeof payload !== 'object' || payload === null) {
    return undefined;
  }

  const payloadPayload =
    'payload' in payload &&
    typeof payload.payload === 'object' &&
    payload.payload !== null
      ? payload.payload
      : undefined;

  let configLabelKey: string = key;

  if (
    key in payload &&
    typeof payload[key as keyof typeof payload] === 'string'
  ) {
    configLabelKey = payload[key as keyof typeof payload] as string;
  } else if (
    payloadPayload &&
    key in payloadPayload &&
    typeof payloadPayload[key as keyof typeof payloadPayload] === 'string'
  ) {
    configLabelKey = payloadPayload[
      key as keyof typeof payloadPayload
    ] as string;
  }

  return configLabelKey in config
    ? config[configLabelKey]
    : config[key as keyof typeof config];
}

ChartContainer.displayName = 'ChartContainer';
ChartTooltipContent.displayName = 'ChartTooltipContent';
ChartLegendContent.displayName = 'ChartLegendContent';

export {
  ChartContainer,
  ChartLegend,
  ChartLegendContent,
  ChartStyle,
  ChartTooltip,
  ChartTooltipContent,
  getVisiblePayloadItems,
};
